import { useCallback, useRef, useState } from "react";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import { useWhiteLabelAddon } from "./useAgencyFeatureAddons";
import {
  FEATURE_ADDON_KEYS,
  startCreateFeatureAddonCheckout,
} from "../actions/featureAddons";
import { SimpleAlert } from "../components/Alerts/Alerts";
import { ALERT_ICON_TYPE_ERROR } from "../utils/constants";
import {
  WHITE_LABEL_TIER_ID,
  canManageAgencyBilling,
} from "../utils/whiteLabelAddon";
import { errorCodeToText, hasKnownErrorTranslation } from "../utils/errors";

/**
 * White-label checkout. Stripe opens directly.
 * The tier paywall does not apply: there is one price.
 */
const useWhiteLabelPurchase = () => {
  const dispatch = useDispatchApp();
  const authUser = useSelectorApp((state) => state.auth.user);
  const { status, whiteLabel, isLoading, refreshStatus } = useWhiteLabelAddon({
    enabled: Boolean(authUser?.uid),
  });
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const inFlight = useRef(false);

  const canManage = canManageAgencyBilling({
    role: authUser?.role,
    agencyID: authUser?.agencyID,
    agenciesAvailable: authUser?.agenciesAvailable,
  });

  const startCheckout = useCallback(async () => {
    if (!canManage || inFlight.current) {
      return { ok: false };
    }
    inFlight.current = true;
    setIsCheckingOut(true);
    try {
      const result = await dispatch(
        startCreateFeatureAddonCheckout({
          addonKey: FEATURE_ADDON_KEYS.WHITE_LABEL,
          tierId: WHITE_LABEL_TIER_ID,
          origin: window.location.origin,
        })
      );
      if (result?.ok && result?.data?.paymentUrl) {
        window.location.assign(result.data.paymentUrl);
      } else if (!result?.ok) {
        const translated = hasKnownErrorTranslation(result?.code)
          ? errorCodeToText(result.code)
          : "";
        const title = translated || result?.message;
        if (title) {
          SimpleAlert({
            title,
            icon: ALERT_ICON_TYPE_ERROR,
          });
        }
      }
      return result || { ok: false };
    } finally {
      inFlight.current = false;
      setIsCheckingOut(false);
    }
  }, [canManage, dispatch]);

  return {
    startCheckout,
    isCheckingOut,
    canManage,
    status,
    whiteLabel,
    isLoading,
    refreshStatus,
  };
};

export default useWhiteLabelPurchase;
